import React, { useState } from 'react';
import { Table, Typography, Modal, Button, Radio, Input, Form, Tooltip, Dropdown, Menu, Tag, Badge, Alert } from 'antd';
import { DownOutlined, MessageOutlined, CommentOutlined } from '@ant-design/icons';
import RemarksPanel from '../../RemarksPanel';
import { axiosInstant, getProfileByUserId } from 'src/api';
import { notification } from 'antd';
import { renderFeedback } from '../../RecruitersPanel/helper/misc';
import { useUser } from "src/context/auth.context";
import { formatInterviewDateTime } from "src/Components/common";
import { ChatBadge } from "src/Components/common/ChatBadge";
import { useChatUnreadCounts } from "src/Components/common/useChatUnreadCounts";

interface PreScreeningQuestion {
  question: string;
  options?: string[];
}

interface ApplicationRecord {
  _id: string;
  companyName: string;
  position: string;
  status: string;
  dateApplied: string;
  interviewDate?: string;
  secondInterviewDate?: string;
  preScreeningQuestions?: PreScreeningQuestion[];
  matchingPercentage?: number;
  feedback?: string;
  withdrawalReason?: string;
  predefinedQuestions?: PreScreeningQuestion[];
  customQuestions?: PreScreeningQuestion[];
  job?: {
    predefinedQuestions?: PreScreeningQuestion[];
    customQuestions?: PreScreeningQuestion[];
  };
  offerLetter?: string;
  [key: string]: any;
}

interface JobApplicationsTableProps {
  applications: ApplicationRecord[];
  onSendApplication: (id: string, answers?: any) => void;
  refetchApplications?: () => void;
}

// Backend status codes to display label and color for candidates
const candidateStatusDisplay: { [key: string]: { label: string; color: string } } = {
  pending:                  { label: 'Pending', color: 'default' },
  applied:                  { label: 'Applied', color: 'purple' },
  shortlisted_recruiter:    { label: 'Shortlisted by Recruiter', color: 'cyan' },
  shortlisted_by_recruiter: { label: 'Shortlisted by Recruiter', color: 'cyan' },
  interviewing_recruiter:   { label: 'Interview with Recruiter', color: 'blue' },
  interviewing:             { label: 'Interviewing', color: 'blue' },
  rejected_recruiter:       { label: 'Rejected by Recruiter', color: 'red' },
  rejected_by_recruiter:    { label: 'Rejected by Recruiter', color: 'red' },
  submitted:                { label: 'Submitted to Manager', color: 'purple' },
  under_review:             { label: 'Under Review', color: 'processing' },
  pending_manager:          { label: 'Pending Manager Review', color: 'default' },
  shortlisted_manager:      { label: 'Shortlisted by Manager', color: 'cyan' },
  shortlisted_by_manager:   { label: 'Shortlisted by Manager', color: 'cyan' },
  interviewing_manager:     { label: 'Interview with Manager', color: 'blue' },
  approved_manager:         { label: 'Approved by Manager', color: 'green' },
  approved_by_manager:      { label: 'Approved by Manager', color: 'green' },
  rejected_manager:         { label: 'Rejected by Manager', color: 'red' },
  rejected_by_manager:      { label: 'Rejected by Manager', color: 'red' },
  hired:                    { label: 'Hired', color: 'green' },
  withdrawn:                { label: 'Withdrawn', color: 'red' },
  offer_placed:             { label: 'Offer Placed', color: 'orange' },
  offer_accepted:           { label: 'Offer Accepted', color: 'green' },
  offer_rejected:           { label: 'Offer Rejected', color: 'red' },
  offer_revoked:            { label: 'Offer Revoked', color: 'red' },
  offer_expired:            { label: 'Offer Expired', color: 'red' },
  on_hold:                  { label: 'On Hold', color: 'gold' },
};


// Applications that already existed when the complete-profile rule shipped keep
// the older four-field check. Candidates who applied while linkedin, location,
// relocation and the rest were optional would otherwise be blocked mid-flow by
// fields that were not asked of them at the time.
const FULL_PROFILE_REQUIRED_FROM = Date.parse('2026-09-17T00:00:00Z');

// JobApplication has no createdAt and no timestamps on its schema, so creation
// time comes from the ObjectId: its first 4 bytes are a unix timestamp in
// seconds. Anything that is not a real ObjectId - the table substitutes a
// `noid-*` placeholder when an id is missing - is treated as pre-existing, so an
// unknown age can never block a candidate.
const applicationCreatedAt = (id?: string): number | null =>
  id && /^[0-9a-f]{24}$/i.test(id) ? parseInt(id.slice(0, 8), 16) * 1000 : null;

const JobApplicationsTable: React.FC<JobApplicationsTableProps> = ({ applications, onSendApplication, refetchApplications }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<ApplicationRecord | null>(null);
  const [answers, setAnswers] = useState<{ [key: string]: string }>({});
  const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawAppId, setWithdrawAppId] = useState<string | null>(null);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [feedbackValue, setFeedbackValue] = useState('');
  const [feedbackAppId, setFeedbackAppId] = useState<string | null>(null);
  const [currentRecruiterFeedback, setCurrentRecruiterFeedback] = useState<string>('');
  const [viewFeedback, setViewFeedback] = useState<string | null>(null);
  const [currentCandidateFeedback, setCurrentCandidateFeedback] = useState<string>('');
  const [recruiterFeedbackValue, setRecruiterFeedbackValue] = useState('');
  const [chatModalVisible, setChatModalVisible] = useState(false);
  const [chatJobApplicationId, setChatJobApplicationId] = useState<string | null>(null);
  const [offerModal, setOfferModal] = useState<{ visible: boolean; record: ApplicationRecord | null }>({ visible: false, record: null });
  const { user } = useUser();
  const unreadCount = useChatUnreadCounts('recruiter-candidate');

  // Whether the candidate has offered any interview time still ahead. Submitting an
  // application needs it, alongside a complete profile and answered questions.
  // null = not known yet.
  const [hasAvailability, setHasAvailability] = useState<boolean | null>(null);
  const checkAvailability = async (): Promise<boolean> => {
    try {
      const { data } = await axiosInstant.get('/calendar/availability-status/mine');
      setHasAvailability(!!data?.hasAvailability);
      return !!data?.hasAvailability;
    } catch {
      setHasAvailability(null);
      return true; // unknown: let the server decide rather than block on a failed lookup
    }
  };
  const goToCalendar = () => { window.location.href = '/calendar'; };
  React.useEffect(() => {
    checkAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  console.log('applications:', applications);

  React.useEffect(() => {
    // Also listen for openChat events (from notification clicks) to open the chat
    const handleOpenChat = (event: any) => {
      const { chatType, jobApplicationId } = event.detail || {};
      if (chatType === 'recruiter-candidate' && jobApplicationId) {
        console.log('✅ Opening recruiter-candidate chat for:', jobApplicationId);
        setChatJobApplicationId(jobApplicationId);
        setChatModalVisible(true);
      }
    };

    window.addEventListener('openChat', handleOpenChat);
    
    return () => {
      window.removeEventListener('openChat', handleOpenChat);
    };
  }, []);

  const handleOpenPreScreening = (record: ApplicationRecord) => {
    console.log('Opening modal for record:', record);
    setCurrentRecord(record);
    setAnswers({});
    setModalVisible(true);
    checkAvailability();
  };

  const handleAnswerChange = (question: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [question]: value }));
  };

  const handleSubmitPreScreening = async () => {
    if (currentRecord) {
      // Check if profile is complete
      try {
        console.log('Current user for profile check:', user); // Debug log
        
        const profile = await getProfileByUserId(user._id);
        
        console.log('Profile data for validation:', profile);
        
        // Profile validation - now using correct endpoint with userId

        // The whole profile has to be complete before an application can be
        // submitted, resume included. These mirror the required fields on the
        // profile form, so anything that saves there satisfies this.
        // isSearching is deliberately absent: it is a boolean and `false` is a
        // valid answer, so it can never be "missing".
        const createdAt = applicationCreatedAt(currentRecord?._id);
        const isNewApplication =
          createdAt !== null && createdAt >= FULL_PROFILE_REQUIRED_FROM;

        const requiredFields = isNewApplication
          ? [
              'firstName', 'lastName', 'email', 'phoneNumber', 'linkedin',
              'positionTitle', 'yearsOfExperience', 'jobPreference', 'location',
              'relocation', 'resume'
            ]
          : ['firstName', 'lastName', 'email', 'positionTitle'];

        const missingFields = requiredFields.filter(field => {
          const value = profile[field];
          // 0 is a legitimate answer for yearsOfExperience, so test for absence
          // rather than falsiness - `!0` would report a complete profile as
          // incomplete and block the candidate with nothing to fix.
          if (value === undefined || value === null) return true;
          if (typeof value === 'string') return value.trim() === '';
          return false;
        });
        
        console.log('Missing fields:', missingFields);
        
        if (missingFields.length > 0) {
          notification.error({
            message: 'Profile Incomplete',
            description: (
              <div>
                <p>Please complete your profile before applying.</p>
                <p>Missing: {missingFields.join(', ')}</p>
                <Button 
                  type="primary" 
                  size="small"
                  onClick={() => window.location.href = '/candidate/profile'}
                  style={{ marginTop: 8 }}
                >
                  Complete Profile
                </Button>
              </div>
            ),
            duration: 0, // Don't auto-close
          });
          return;
        }
      } catch (error) {
        console.error('Profile validation error:', error);
        notification.error({
          message: 'Profile Check Failed',
          description: 'Unable to verify profile completion. Please ensure your profile is complete.',
        });
        return;
      }
      
      // Interview availability: without it the recruiter has nothing to schedule against.
      if (!(await checkAvailability())) {
        notification.warning({
          message: 'Add your availability',
          description: (
            <div>
              <p>Please add the times you are available for an interview before submitting.</p>
              <Button type="primary" size="small" onClick={goToCalendar} style={{ marginTop: 8 }}>
                Add availability
              </Button>
            </div>
          ),
          duration: 0,
        });
        return;
      }

      const questions = [
        ...(currentRecord?.job?.predefinedQuestions || []),
        ...(currentRecord?.job?.customQuestions || [])
      ].filter(q => {
        const text = typeof q === 'string' ? q : (q as any)?.question;
        return text && text.trim().length > 0;
      });
      const unanswered = questions.filter(q => {
        const questionObj = typeof q === 'string' ? { question: q } : q;
        return !answers[questionObj.question] || answers[questionObj.question].trim() === '';
      });
      if (unanswered.length > 0) {
        notification.warning({
          message: 'Incomplete',
          description: 'Please answer all pre-screening questions before submitting.'
        });
        return;
      }
      const answersArray = Object.entries(answers).map(([question, answer]) => ({ question, answer }));
      try {
        await axiosInstant.patch(
          `/job-applications/${currentRecord._id}/pre-screening-answers`,
          { answers: answersArray }
        );
        // Now submit the application to trigger notifications
        await axiosInstant.post(
          `/job-applications/applications/${currentRecord._id}/submit`,
          {}
        );
        notification.success({
          message: 'Application Submitted',
          description: 'Your application has been successfully submitted!'
        });
        if (refetchApplications) {
          refetchApplications();
        }
        setModalVisible(false);
      } catch (error: any) {
        console.error(error);
        if (error?.response?.data?.code === 'AVAILABILITY_REQUIRED') {
          setHasAvailability(false);
          notification.warning({
            message: 'Add your availability',
            description: error.response.data.message,
            btn: <Button type="primary" size="small" onClick={goToCalendar}>Add availability</Button>,
            duration: 0,
          });
        }
      }
    }
  };

  const formattedDateApplied = currentRecord?.dateApplied
    ? new Date(currentRecord.dateApplied).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '-';

  // No status filter here on purpose. This used to be an allow-list
  // (candidateVisibleStatuses) that had to be kept in sync by hand with every
  // status the backend can set - and it fell out of sync: 'approved_manager'
  // was never added, so the moment a hiring manager approved a candidate, that
  // application silently vanished from the candidate's own dashboard. It was
  // never deleted - GET /job-applications/my-applications already returns every
  // application for this candidate with no status filter of its own - it was
  // only ever hidden here. A candidate's application must never disappear from
  // their dashboard, for any status, including once hired, so show all of them.
  const candidateApplications = applications;

  const columns = [
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
      align: 'center' as const,
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      align: 'center' as const,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center' as const,
      render: (status: string) => {
        const { label, color } = candidateStatusDisplay[status] || { 
          label: status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown', 
          color: 'default' 
        };
        return (
          <Tag 
            color={color} 
            style={{ 
              fontSize: '14px', 
              padding: '4px 12px',
              borderRadius: '4px',
            }}
          >
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'Date Applied',
      dataIndex: 'dateApplied',
      key: 'dateApplied',
      align: 'center' as const,
      render: (date: string) =>
        date
          ? new Date(date).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })
          : '-',
    },
    {
      title: '1st Interview Date',
      dataIndex: 'interviewDate',
      key: 'interviewDate',
      align: 'center' as const,
      render: (date: string) => (date ? formatInterviewDateTime(date) : '-'),
    },
    {
      title: '2nd Interview Date',
      dataIndex: 'secondInterviewDate',
      key: 'secondInterviewDate',
      align: 'center' as const,
      render: (date: string) => (date ? formatInterviewDateTime(date) : '-'),
    },
    {
      title: 'Matching %',
      dataIndex: 'screeningScore',
      key: 'screeningScore',
      align: 'center' as const,
      render: (score: number) => {
        const getScoreColor = (score: number) => {
          if (score >= 80) return '#52c41a'; // Green
          if (score >= 60) return '#faad14'; // Orange
          return '#ff4d4f'; // Red
        };

        if (score !== undefined) {
          return (
            <Badge 
              count={`${score}%`} 
              style={{ 
                backgroundColor: getScoreColor(score),
                color: 'white',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            />
          );
        }

        return '-';
      },
    },
    {
      title: 'Offer',
      key: 'offer',
      align: 'center' as const,
      render: (_: any, record: ApplicationRecord) =>
        record.offerLetter ? (
          <Button 
            onClick={() => openOfferModal(record)}
            style={{ fontSize: '16px', height: '40px', padding: '0 20px' }}
          >
            View Offer
          </Button>
        ) : (
          <span>—</span>
        ),
    },
    {
      title: 'Feedback',
      key: 'feedback',
      align: 'center' as const,
      render: (_: any, record: ApplicationRecord) => (
        <Button
          onClick={() => {
            setFeedbackAppId(record._id);
            setRecruiterFeedbackValue(record.feedback ?? '');
            setCurrentRecruiterFeedback(record.feedback ?? '');
            setCurrentCandidateFeedback(record.candidateFeedback ?? '');
            setFeedbackModalVisible(true);
          }}
          style={{ fontSize: '16px', height: '40px', padding: '0 20px' }}
        >
          Feedback
        </Button>
      ),
    },
    {
      title: 'Chat',
      key: 'chat',
      align: 'center' as const,
      render: (_: any, record: ApplicationRecord) => (
        <Tooltip title={`Chat with recruiter${unreadCount[record._id] > 0 ? ` (${unreadCount[record._id]} unread)` : ''}`}>
          <ChatBadge count={unreadCount[record._id] || 0} offset={[-8, 8]}>
            <Button
              icon={<CommentOutlined style={{ fontSize: '20px' }} />}
              onClick={() => {
                setChatJobApplicationId(record._id);
                setChatModalVisible(true);
                axiosInstant.post('/remarks/mark-read', { userId: user?._id, jobApplicationId: record._id });
              }}
              style={{ height: '40px', width: '40px' }}
            />
          </ChatBadge>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center' as const,
      render: (_: any, record: ApplicationRecord) => {
        const disableSubmit = [
          'applied',
          'pending_manager',
          'interviewing',
          'interviewing_recruiter',
          'interviewing_manager',
          'rejected_recruiter',
          'rejected_manager',
          'under_review',
          'shortlisted_manager',
          'shortlisted_recruiter',
          'hired',
          // ...any other statuses that mean "already submitted"
        ].includes(record.status);
        const enableWithdraw = [
          'applied',
           'shortlisted_recruiter',
           'shortlisted_manager',
           'pending_manager',
           'submitted',
           'under_review',
           'interviewing',
           'interviewing_recruiter',
           'interviewing_manager',
           'rejected_recruiter',
           'rejected_manager',
           'hired',
            ].includes(record.status);
        return (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="withdraw"
                  disabled={!enableWithdraw}
                  onClick={() => {
                    setWithdrawAppId(record._id);
                    setWithdrawModalVisible(true);
                  }}
                  style={{ fontSize: '16px' }}
                >
                  Withdraw Application
                </Menu.Item>
                <Menu.Item
                  key="submit"
                  disabled={disableSubmit}
                  onClick={() => handleOpenPreScreening(record)}
                  style={{ fontSize: '16px' }}
                >
                  Submit Application
                </Menu.Item>
              </Menu>
            }
          >
            <Button style={{ fontSize: '16px', height: '40px', padding: '0 20px' }}>
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const openOfferModal = (record: ApplicationRecord) => {
    setOfferModal({ visible: true, record });
  };

  const acceptOffer = async (applicationId: string) => {
    await axiosInstant.patch(`/job-applications/applications/${applicationId}/accept-offer`);
    setOfferModal({ visible: false, record: null });
    if (refetchApplications) refetchApplications();
    notification.success({ message: "Offer accepted!" });
  };

  const rejectOffer = async (applicationId: string) => {
    await axiosInstant.patch(`/job-applications/applications/${applicationId}/reject-offer`);
    setOfferModal({ visible: false, record: null });
    if (refetchApplications) refetchApplications();
    notification.success({ message: "Offer rejected." });
  };

  return (
    <div className="max-w-7xl mx-auto py-8 px-12">
      {hasAvailability === false && candidateApplications.some((a: any) => a.status === 'pending') && (
        <Alert
          type="warning"
          showIcon
          style={{ margin: '0 16px 24px' }}
          message="Add your available times to submit your applications"
          description="Besides a complete profile and answered pre-screening questions, you need to offer times you are free for an interview."
          action={<Button type="primary" onClick={goToCalendar}>Add availability</Button>}
        />
      )}
      <div className="mb-8">
        <Typography.Title level={2} style={{ marginLeft: '16px', fontSize: '36px', fontWeight: 'bold', color: '#2f54eb' }}>Job Applications</Typography.Title>
        <Typography.Text type="secondary" style={{ marginLeft: '16px', display: 'block', fontSize: '16px' }}>
          Track the status of your job applications and manage your interviews
        </Typography.Text>
      </div>
      <div className="bg-white rounded-lg shadow">
        <Table
          columns={columns.map(col => ({
            ...col,
            title: <span style={{ fontSize: '18px', fontWeight: 600 }}>{col.title}</span>,
            render: col.render ? (text: string | number, record: ApplicationRecord) => {
              const rendered = (col.render as (text: string | number, record: ApplicationRecord) => React.ReactNode)(text, record);
              if (React.isValidElement(rendered)) {
                return React.cloneElement(rendered as React.ReactElement<any>, {
                  style: { ...(rendered as React.ReactElement<any>).props.style, fontSize: '16px' }
                });
              }
              return <span style={{ fontSize: '16px' }}>{rendered}</span>;
            } : (text: string | number) => <span style={{ fontSize: '16px' }}>{text}</span>
          }))}
          dataSource={candidateApplications}
          rowKey="_id"
                  pagination={{ pageSize: 10, style: { fontSize: '16px' } }}
          className="p-4"
        />
      </div>
      <Modal
        title={<span style={{ fontSize: '24px', fontWeight: 'bold' }}>Pre-screening Questions</span>}
        visible={modalVisible}
        onOk={handleSubmitPreScreening}
        onCancel={() => setModalVisible(false)}
      >
        {hasAvailability === false && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Add your available times first"
            description="The recruiter needs times you are free for an interview before you can submit."
            action={<Button size="small" type="primary" onClick={goToCalendar}>Add availability</Button>}
          />
        )}
        <Form layout="vertical" style={{ fontSize: '16px' }}>
          {[
            ...(currentRecord?.job?.predefinedQuestions || []),
            ...(currentRecord?.job?.customQuestions || [])
          ].filter(q => {
            const text = typeof q === 'string' ? q : q?.question;
            return text && text.trim().length > 0;
          }).map((q, idx) => {
            const questionObj = typeof q === 'string' ? { question: q } : q;
            return (
              <Form.Item label={<span style={{ fontSize: '16px' }}>{questionObj.question}</span>} key={idx} required>
                {questionObj.options && questionObj.options.length > 0 ? (
                  <Radio.Group
                    onChange={e => handleAnswerChange(questionObj.question, e.target.value)}
                    value={answers[questionObj.question]}
                    style={{ fontSize: '16px' }}
                  >
                    {questionObj.options.map((opt, i) => (
                      <Radio key={i} value={opt} style={{ fontSize: '16px' }}>
                        {opt}
                      </Radio>
                    ))}
                  </Radio.Group>
                ) : (
                  <Input
                    value={answers[questionObj.question]}
                    onChange={e => handleAnswerChange(questionObj.question, e.target.value)}
                    placeholder="Type your answer"
                    style={{ fontSize: '16px' }}
                  />
                )}
              </Form.Item>
            );
          })}
        </Form>
      </Modal>
      <Modal
        title={<span style={{ fontSize: '24px', fontWeight: 'bold' }}>Withdraw Application</span>}
        visible={withdrawModalVisible}
        onOk={async () => {
          await axiosInstant.patch(`/job-applications/withdraw/${withdrawAppId}`, { reason: withdrawReason });
          setWithdrawModalVisible(false);
          setWithdrawReason('');
          setWithdrawAppId(null);
          if (refetchApplications) refetchApplications();
          notification.success({ message: 'Application withdrawn successfully.' });
        }}
        onCancel={() => {
          setWithdrawModalVisible(false);
          setWithdrawReason('');
          setWithdrawAppId(null);
        }}
        okText="Withdraw"
        okButtonProps={{ danger: true, style: { fontSize: '16px' } }}
      >
        <div style={{ fontSize: '16px' }}>
          <label>Please state your reason for withdrawal:</label>
          <Input.TextArea
            value={withdrawReason}
            onChange={e => setWithdrawReason(e.target.value)}
            rows={4}
            placeholder="Enter your reason"
            style={{ fontSize: '16px' }}
          />
        </div>
      </Modal>
      <Modal
        title={<span style={{ fontSize: '24px', fontWeight: 'bold' }}>Recruiter Feedback</span>}
        visible={!!viewFeedback}
        onOk={() => setViewFeedback(null)}
        onCancel={() => setViewFeedback(null)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setViewFeedback(null)} style={{ fontSize: '16px' }}>
            OK
          </Button>
        ]}
      >
        <div style={{ fontSize: '16px' }}>{viewFeedback}</div>
      </Modal>
      <Modal
        title={<span style={{ fontSize: '24px', fontWeight: 'bold' }}>Feedback</span>}
        visible={feedbackModalVisible}
        onOk={async () => {
          if (feedbackValue.trim()) {
            await axiosInstant.patch(`/job-applications/${feedbackAppId}/candidate-feedback`, {
              feedback: feedbackValue,
            });
            notification.success({ message: 'Your feedback has been submitted.' });
          }
          setFeedbackModalVisible(false);
          setFeedbackValue('');
          setFeedbackAppId(null);
          setCurrentRecruiterFeedback('');
          if (refetchApplications) refetchApplications();
        }}
        onCancel={() => {
          setFeedbackModalVisible(false);
          setFeedbackValue('');
          setFeedbackAppId(null);
          setCurrentRecruiterFeedback('');
        }}
        okText="Submit"
        okButtonProps={{ disabled: !!currentCandidateFeedback, style: { fontSize: '16px' } }}
      >
        <div style={{ marginBottom: 16, fontSize: '16px' }}>
          <label><b>Feedback from Recruiter:</b></label>
          <div
            style={{
              minHeight: 60,
              background: '#f5f5f5',
              border: '1px solid #eee',
              borderRadius: 4,
              padding: 8,
              marginTop: 4,
              marginBottom: 16,
              color: currentRecruiterFeedback ? '#222' : '#888',
              fontSize: '16px'
            }}
          >
            {currentRecruiterFeedback
              ? renderFeedback(currentRecruiterFeedback)
              : 'No feedback from recruiter yet.'}
          </div>
        </div>
        <div style={{ fontSize: '16px' }}>
          <label><b>Your Feedback for Recruiter:</b></label>
          <Input.TextArea
            value={currentCandidateFeedback || feedbackValue}
            onChange={e => setFeedbackValue(e.target.value)}
            rows={4}
            placeholder="Enter your feedback"
            disabled={!!currentCandidateFeedback}
            style={{ fontSize: '16px' }}
          />
        </div>
      </Modal>
      {chatModalVisible && chatJobApplicationId && (
        <RemarksPanel
          candidateId={chatJobApplicationId}
          jobApplicationId={chatJobApplicationId}
          type="recruiter-candidate"
          visible={chatModalVisible}
          onCancel={() => {
            setChatModalVisible(false);
            setChatJobApplicationId(null);
          }}
        />
      )}
      <Modal
        title="Job Offer"
        open={offerModal.visible}
        onCancel={() => setOfferModal({ visible: false, record: null })}
        footer={null}
      >
        {offerModal.record && offerModal.record.offerLetter && (
          <div>
            <b>Offer Letter:</b>
            <pre style={{ whiteSpace: 'pre-wrap' }}>{offerModal.record.offerLetter}</pre>
            {offerModal.record.offerAccepted && (
              <div style={{ color: "green", marginTop: 8 }}>
                Offer Accepted {offerModal.record.offerAcceptedDate && `on ${new Date(offerModal.record.offerAcceptedDate).toLocaleDateString()}`}
              </div>
            )}
            {offerModal.record.offerRejected && (
              <div style={{ color: "red", marginTop: 8 }}>
                Offer Rejected {offerModal.record.offerRejectedDate && `on ${new Date(offerModal.record.offerRejectedDate).toLocaleDateString()}`}
              </div>
            )}
            {!offerModal.record.offerAccepted && !offerModal.record.offerRejected && (
              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  onClick={() => offerModal.record && acceptOffer(offerModal.record._id)}
                  style={{ marginRight: 8 }}
                >
                  Accept Offer
                </Button>
                <Button
                  danger
                  onClick={() => offerModal.record &&  rejectOffer(offerModal.record._id)}
                >
                  Reject Offer
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default JobApplicationsTable; 